import React from "react";
import { PictureEditorProps } from "./PictureEditor.types";
import { VeriModal } from "../../modals/VeriModal";
import AvatarEditorCropper, { Position } from "react-avatar-editor";
import {
  Box,
  Flex,
  HStack,
  Slider,
  SliderFilledTrack,
  SliderThumb,
  SliderTrack,
} from "@chakra-ui/react";
import { MinusIcon, PlusIcon } from "../../../icons";
import { CustomHeading } from "../CustomHeading";
import { Button } from "../Button";
import AvatarEditor from "react-avatar-editor";
import { useVeriToast } from "../../../hooks";

export const PictureEditor: React.FC<PictureEditorProps> = ({
  maskColor,
  file,
  onComplete,
  pictureToEdit,
  cropWidth,
  cropHeight,
  ...rest
}) => {
  const { toastError } = useVeriToast();
  const editor = React.useRef<AvatarEditor>(null);
  const [position, setPosition] = React.useState<Position>(
    pictureToEdit?.position || { x: 0, y: 0 },
  );
  const [scale, setScale] = React.useState(
    pictureToEdit?.scale !== undefined ? pictureToEdit?.scale : 100,
  );

  let pictureToEditUrl = file?.file ? URL.createObjectURL(file.file) : "";
  let originalFile = file;

  if (pictureToEdit) {
    if (pictureToEdit.file?.file) {
      originalFile = pictureToEdit.file;
      pictureToEditUrl = URL.createObjectURL(pictureToEdit.file.file);
    } else {
      pictureToEditUrl = pictureToEdit.url || "";
    }
  }

  const handleComplete = async () => {
    if (onComplete) {
      editor.current?.getImageScaledToCanvas().toBlob((blob) => {
        if (!blob) {
          toastError("Something went wrong generating the image");
          return;
        }
        onComplete({
          originalFile,
          originalUrl: pictureToEditUrl,
          croppedImage: blob,
          scale,
          position: position as Position,
          url: URL.createObjectURL(blob),
        });
      });
    }
  };

  return (
    <VeriModal size={"5xl"} {...rest}>
      <Box mb={4}>
        <CustomHeading
          textStyle={"h2"}
          subtitle={
            <Box mt={3}>
              Drag the image to change placement. The lighter colored area will
              be cropped off.
            </Box>
          }
        >
          Adjust image
        </CustomHeading>
      </Box>
      <Flex
        flexDirection={"column"}
        alignItems={"center"}
        margin={"0 auto"}
        w={"100%"}
        maxWidth={"1200px"}
      >
        <Box bg={"url(/images/transparency-pattern.png)"}>
          <AvatarEditorCropper
            image={pictureToEditUrl}
            width={cropWidth}
            height={cropHeight}
            border={15}
            color={maskColor || [255, 255, 255, 0.8]} // RGBA
            scale={scale ? scale / 100 : 1}
            rotate={0}
            borderRadius={0}
            position={position}
            onPositionChange={function (position) {
              setPosition(position);
            }}
            ref={editor}
          />
        </Box>

        <Flex alignItems={"center"} w={"100%"} mt={6}>
          <MinusIcon color={"text.grey"} boxSize={8} />
          <Slider
            defaultValue={100}
            min={10}
            max={200}
            step={1}
            onChange={(value) => setScale(value)}
            colorScheme="brand"
            mx={8}
            value={scale}
          >
            <SliderTrack h={0.5}>
              <Box position="relative" right={10} />
              <SliderFilledTrack h={0.5} />
            </SliderTrack>
            <SliderThumb
              boxSize={8}
              boxShadow="0px 1px 2px rgba(0, 0, 0, 0.06), 0px 4px 9px rgba(0, 0, 0, 0.1)"
              _focus={{
                boxShadow:
                  "0px 1px 2px rgba(0, 0, 0, 0.06), 0px 4px 9px rgba(0, 0, 0, 0.1)!important",
              }}
            />
          </Slider>
          <PlusIcon color={"text.grey"} boxSize={6} />
        </Flex>
      </Flex>
      <HStack spacing={2} mt={4}>
        <Button onClick={handleComplete} isLarge>
          Save changes
        </Button>
        <Button onClick={rest.onClose} isLarge outline>
          Cancel
        </Button>
      </HStack>
    </VeriModal>
  );
};
